/**
 * Senders list or AI previews for ApplyRuleBasedSendersSection.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { AnimatePresence } from 'framer-motion';

import { GeneratedSendersPreview } from '@/features/campaign-texts/components/GeneratedSendersPreview';
import { GeneratedSendersPreviewPerDrop } from '@/features/campaign-texts/components/GeneratedSendersPreviewPerDrop';
import { SenderPickerItem } from '@/features/smswiser-campaigns/components/drop-modals/CreativePickerPrimitives';

export interface SendersListAndPreviewsProps {
  perDropBatches: string[][] | null;
  generatedSenders: string[];
  dropLabels?: Array<{ id: string; label: string }>;
  dropCount?: number;
  filteredSenders: string[];
  selectedSenders: string[];
  onToggleSender: (sender: string) => void;
  hasAiGenerated: boolean;
  onSavePerDrop: (batches: string[][]) => void | Promise<void>;
  onDismissPerDrop: () => void;
  onRegeneratePerDrop: (dropIdx: number, indices: number[]) => void;
  onRegenerateAllPerDrop: (dropIndices: Array<{ dropIdx: number; indices: number[] }>) => void | Promise<void>;
  onSaveAndHidePerDrop: (batches: string[][], unselectedSenders: string[]) => void | Promise<void>;
  onSaveGenerated: (senders: string[]) => void;
  onDismiss: () => void;
  onRegenerate: (indices: number[]) => void;
  isRegenerating: boolean;
  perDropApplied: boolean;
}

export const SendersListAndPreviews: FC<SendersListAndPreviewsProps> = (
  props,
) => {
  const defaultDropLabels =
    props.dropLabels ??
    Array.from({ length: props.dropCount ?? 0 }, (_, i) => ({
      id: String(i),
      label: `List ${i + 1}`,
    }));

  return (
    <>
      <AnimatePresence>
        {props.perDropBatches && props.perDropBatches.length > 0 ? (
          <GeneratedSendersPreviewPerDrop
            batches={props.perDropBatches}
            dropLabels={defaultDropLabels}
            onSave={props.onSavePerDrop}
            onDismiss={props.onDismissPerDrop}
            onRegenerate={props.onRegeneratePerDrop}
            onRegenerateAll={props.onRegenerateAllPerDrop}
            onSaveAndHide={props.onSaveAndHidePerDrop}
            isRegenerating={props.isRegenerating}
            applied={props.perDropApplied}
          />
        ) : props.generatedSenders.length > 0 ? (
          <GeneratedSendersPreview
            senders={props.generatedSenders}
            onSave={props.onSaveGenerated}
            onDismiss={props.onDismiss}
            onRegenerate={props.onRegenerate}
            isRegenerating={props.isRegenerating}
            compact
          />
        ) : null}
      </AnimatePresence>

      {!props.hasAiGenerated && (
        <div className="max-h-[min(280px,45vh)] min-h-[80px] space-y-1 overflow-y-auto rounded-lg border border-border bg-muted/10 p-2">
          {props.filteredSenders.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">
              No senders. Add manually or use AI to generate.
            </p>
          ) : (
            props.filteredSenders.map((sender) => (
              <SenderPickerItem
                key={sender}
                label={sender}
                isSelected={props.selectedSenders.includes(sender)}
                onToggle={() => props.onToggleSender(sender)}
              />
            ))
          )}
        </div>
      )}
    </>
  );
};
